/**
 * UUIDv7 generator (RFC 9562 §5.7) for Idempotency_Keys (Req 13.1).
 *
 * Layout: 48-bit Unix ms timestamp | version 7 | 12-bit `rand_a` | variant 10 | 62-bit
 * `rand_b`, randomness from `crypto.getRandomValues`. `rand_a` is used as a monotonic
 * counter within one millisecond (RFC 9562 §6.2, method 1), and the timestamp never goes
 * backwards, so keys from one generator sort lexicographically in creation order. The
 * offline queue relies on that to submit scans in the order they were taken.
 */
export type RandomFill = (bytes: Uint8Array<ArrayBuffer>) => void;

const defaultRandom: RandomFill = (bytes) => void crypto.getRandomValues(bytes);

export type UuidV7Generator = (nowMs: number) => string;

export function createUuidV7(random: RandomFill = defaultRandom): UuidV7Generator {
  let lastMs = -1;
  let seq = 0;
  return (nowMs) => {
    const bytes = new Uint8Array(16);
    random(bytes);
    let ms = Math.max(Math.floor(nowMs), lastMs);
    if (ms === lastMs) {
      seq++;
      if (seq > 0xfff) {
        // Counter exhausted within this millisecond: borrow the next one.
        ms++;
        seq = 0;
      }
    } else {
      // Random start in the lower half leaves room to count up within the millisecond.
      seq = ((bytes[6]! & 0x07) << 8) | bytes[7]!;
    }
    lastMs = ms;
    let t = ms;
    for (let i = 5; i >= 0; i--) {
      bytes[i] = t % 256;
      t = Math.floor(t / 256);
    }
    bytes[6] = 0x70 | (seq >> 8);
    bytes[7] = seq & 0xff;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
}

/** Process-wide generator (one counter per page). */
export const uuidv7: UuidV7Generator = createUuidV7();
